import React from 'react';
import { Briefcase, Search, RefreshCw, AlertTriangle, Globe, FileText, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { LoadingOverlay } from '../../components/ui/LoadingOverlay';
import { useLoading } from '../../hooks/useLoading';
import { sleep } from '../../utils/sleep';

const TrademarkMonitoring = () => {
  const { isLoading, withLoading } = useLoading();

  const handleScan = async () => {
    await withLoading(async () => {
      await sleep(3000);
      console.log('Scanning for trademark violations...');
    });
  };

  return (
    <div className="space-y-6 relative">
      {isLoading && <LoadingOverlay message="Scanning trademarks..." />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Briefcase className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Trademark Monitoring</h1>
            <p className="text-sm text-gray-400">Monitor and protect your trademarks</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={handleScan}>
            <Plus className="h-4 w-4 mr-2" />
            Request Takedown
          </Button>
        </div>
      </div>

      {/* Trademark Stats */}
      <div className="grid grid-cols-4 gap-6">
        {[
          { label: 'Active Trademarks', value: '24', trend: '+2', icon: Briefcase },
          { label: 'Potential Violations', value: '8', trend: '+3', icon: AlertTriangle },
          { label: 'Monitored Regions', value: '12', trend: '+1', icon: Globe },
          { label: 'Filed Reports', value: '45', trend: '+5', icon: FileText }
        ].map((stat) => (
          <div key={stat.label} className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">{stat.label}</span>
              <stat.icon className="h-5 w-5 text-blue-500" />
            </div>
            <div className="flex items-end justify-between">
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className={`text-xs ${stat.trend.startsWith('+') ? 'text-red-500' : 'text-emerald-500'}`}>
                {stat.trend}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Violations */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <h2 className="text-lg font-semibold text-white mb-4">Recent Violations</h2>
        <div className="space-y-4">
          {[
            {
              trademark: 'Example™',
              type: 'Logo Misuse',
              location: 'United States',
              platform: 'E-commerce',
              severity: 'high',
              status: 'investigating'
            },
            {
              trademark: 'Example Pro™',
              type: 'Unauthorized Use',
              location: 'European Union',
              platform: 'Social Media',
              severity: 'medium',
              status: 'reported'
            },
            {
              trademark: 'Example Enterprise™',
              type: 'Counterfeit',
              location: 'Asia Pacific',
              platform: 'Marketplace',
              severity: 'critical',
              status: 'takedown'
            }
          ].map((violation, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className={`p-2 rounded-lg ${
                  violation.severity === 'critical' ? 'bg-red-500/10' :
                  violation.severity === 'high' ? 'bg-orange-500/10' :
                  'bg-yellow-500/10'
                }`}>
                  <AlertTriangle className={`h-5 w-5 ${
                    violation.severity === 'critical' ? 'text-red-500' :
                    violation.severity === 'high' ? 'text-orange-500' :
                    'text-yellow-500'
                  }`} />
                </div>
                <div>
                  <div className="text-sm font-medium text-white">{violation.trademark}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {violation.type} • {violation.location} • {violation.platform}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  violation.status === 'takedown' ? 'bg-red-500/10 text-red-500' :
                  violation.status === 'investigating' ? 'bg-yellow-500/10 text-yellow-500' :
                  'bg-blue-500/10 text-blue-500'
                }`}>
                  {violation.status}
                </span>
                {/* <Button variant="outline" size="sm">Take Action</Button> */}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Monitoring Coverage */}
      <div className="grid grid-cols-2 gap-6">
        {/* Regions */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <h2 className="text-lg font-semibold text-white mb-4">Regional Coverage</h2>
          <div className="space-y-4">
            {[
              { region: 'North America', coverage: 85, violations: 12 },
              { region: 'European Union', coverage: 75, violations: 8 },
              { region: 'Asia Pacific', coverage: 60, violations: 15 }
            ].map((region) => (
              <div key={region.region} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-300">{region.region}</span>
                  <span className="text-white">{region.coverage}%</span>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${region.coverage}%` }}
                  />
                </div>
                <div className="text-xs text-gray-500">
                  {region.violations} active violations
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Platforms */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <h2 className="text-lg font-semibold text-white mb-4">Platform Monitoring</h2>
          <div className="space-y-4">
            {[
              { platform: 'E-commerce', violations: 24, trend: 'up' },
              { platform: 'Social Media', violations: 18, trend: 'stable' },
              { platform: 'Mobile Apps', violations: 12, trend: 'down' }
            ].map((platform) => (
              <div key={platform.platform} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-white">{platform.platform}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {platform.violations} violations detected
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className={`text-xs ${
                    platform.trend === 'up' ? 'text-red-500' :
                    platform.trend === 'down' ? 'text-emerald-500' :
                    'text-gray-500'
                  }`}>
                    {platform.trend === 'up' ? '↑' :
                     platform.trend === 'down' ? '↓' :
                     '→'}
                  </div>
                  <Button variant="outline" size="sm">View Details</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrademarkMonitoring;